import { Box, Typography, useTheme, TextField } from '@mui/material';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { CustomDataGrid, DialogBox } from 'components';
import Buttons from 'components/elements/Buttons';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import { strings } from 'i18n/Strings';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  searchDailyFeeTable,
  fetchFundLevelFees,
} from 'service/FeeCalculationService';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { colorTheme } from 'theme';
import { checkSortingValues, dateFormat } from 'utils/GeneralUtil';

interface IDailyFeeProps {
  dailyFeeId: string;
  fund1DailyFee: string;
  fund2DailyFee: string;
  fund3DailyFee: string;
  date: string;
  userId: string;
  user: { firstName: string; lastName: string; email: string };
  fundId: string;
  fund: { description: string };
  dailyFee: string;
}

interface UserLevelFeesProps {
  userId: number;
  fundId: number;
  date: string;
  page: number;
  size: number;
  sortBy: string;
  sortOrder: string;
}

const DailyFeeCalculation = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const dispatch = useDispatch();
  const [feeData, setFeeData] = useState<IDailyFeeProps[]>([]);
  const [fundId, setFundId] = useState('');
  const [date, setDate] = useState('');
  // const [isLoader, setIsLoader] = useState(false);
  const [pageCount, setPageCount] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [modalData, setModalData] = useState<UserLevelFeesProps[]>([]);
  const [modalPageCount, setModalPageCount] = useState(null);
  const [modalRow, setModalRow] = useState<GridValidRowModel | null>(null);
  const pageInfo = useSelector((state) => state?.pagination?.dailyFee);
  type GridRowData = Record<string, unknown>;

  const modalPageInfo = useSelector(
    (state) => state?.pagination?.dailyFeeModal,
  );

  const handleOpenModal = (row: GridValidRowModel): void => {
    setModalRow(row);
    setOpenModal(true);
  };

  const handleCloseModal = (): void => {
    setOpenModal(false);
    setModalData([]);
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'date',
      width: 150,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Date
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row.date)}
        </Typography>
      ),
    },
    {
      field: 'fundId',
      width: 120,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Fund ID
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.fundId}
        </Typography>
      ),
    },
    {
      field: 'fundName',
      width: 500,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Fund Name
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={13}>
          {row?.fund?.description}
        </Typography>
      ),
    },
    {
      field: 'dailyFee',
      width: 180,
      headerAlign: 'right',
      align: 'right',
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.dailyFee}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          <a
            style={{ color: 'blue', textDecoration: 'underline' }}
            onClick={() => {
              handleOpenModal(row);
            }}
          >
            {CurrencyFormater(Number(row.dailyFee))}
          </a>
        </Typography>
      ),
    },
  ];

  const modalColumns: GridColumns<GridValidRowModel> = [
    {
      field: 'date',
      headerName: 'Date',
      width: 150,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.dateColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.date}
        </Typography>
      ),
    },
    {
      field: 'userId',
      headerName: 'User ID',
      width: 100,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.userId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.userId}
        </Typography>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.nameColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.user?.firstName ?? ''} {row?.user?.lastName ?? ''}
        </Typography>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 200,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.email}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.user?.email ?? '-'}
        </Typography>
      ),
    },
    {
      field: 'fundId',
      headerName: 'Fund ID',
      width: 100,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.fundId ?? '-'}
        </Typography>
      ),
    },
    {
      field: 'fundName',
      headerName: 'Fund Name',
      width: 380,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.fund?.description ?? '-'}
        </Typography>
      ),
    },
    {
      field: 'dailyFee',
      headerName: 'Daily Fee',
      width: 180,
      headerAlign: 'center',
      align: 'center',
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.dailyFee}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormater(Number(row.dailyFee))}
        </Typography>
      ),
    },
  ];

  // const updateFeeData = (): void => {
  //   viewDailyFeeCalculationData(Number(pageInfo?.page) + 1, pageInfo?.pageSize)
  //     .then((dailyFeeData) => {
  //       if (
  //         typeof dailyFeeData !== 'undefined' &&
  //         'basicInfo' in dailyFeeData
  //       ) {
  //         const { basicInfo } = dailyFeeData as { basicInfo: IDailyFeeProps[] };
  //         if (basicInfo) {
  //           const dailyFeeInfo = basicInfo?.rows?.map((entry, index) => {
  //             return { ...entry, id: index };
  //           });

  //           setFeeData(dailyFeeInfo);
  //           setPageCount(basicInfo?.count);
  //         }
  //       }
  //     })
  //     .catch((err: Error) => {
  //       throw err;
  //     });
  // };

  const handleSearch = async (): Promise<void> => {
    try {
      const data = await searchDailyFeeTable({
        fundId: Number(fundId),
        date,
        page: Number(pageInfo?.page) + 1,
        size: pageInfo?.pageSize,
        sortBy: pageInfo?.sortBy,
        sortOrder: pageInfo?.sortOrder,
      });
      setFeeData(data?.result?.rows || []);
      setPageCount(data?.result?.count || 0);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    void handleSearch();
  }, [
    pageInfo?.page,
    pageInfo?.pageSize,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
  ]);

  useEffect(() => {
    const fetchModalData = async (): Promise<void> => {
      if (!modalRow) return;

      const res = await fetchFundLevelFees({
        fundId: modalRow.fundId ?? modalRow.fund?.id,
        date: modalRow.date,
        page: Number(modalPageInfo?.page) + 1,
        size: modalPageInfo?.pageSize,
        sortBy: modalPageInfo?.sortBy,
        sortOrder: modalPageInfo?.sortOrder,
      });
      setModalData(res?.data?.rows ?? []);
      setModalPageCount(res?.data?.count);
    };

    if (openModal) void fetchModalData();
  }, [
    openModal,
    modalPageInfo?.page,
    modalPageInfo?.pageSize,
    modalPageInfo?.sortBy,
    modalPageInfo?.sortOrder,
  ]);

  return (
    <Box>
      <Box display="flex" flexDirection="row" justifyContent="flex-end">
        <TextField
          label={strings.displayText.fundId}
          value={fundId}
          sx={{ mx: 2 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFundId(e.target.value);
          }}
        />
        <TextField
          label="Date"
          type="date"
          value={date}
          sx={{ mx: 2 }}
          placeholder=""
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setDate(e.target.value);
          }}
          InputLabelProps={{ shrink: true }}
        />

        <Box sx={{ alignSelf: 'center' }}>
          {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
          <Buttons
            variant="contained"
            color="primary"
            onClick={() => {
              dispatch(setCurrentPage({ key: 'dailyFee', value: 0 }));
              void handleSearch();
            }}
          >
            {strings.displayText.search}
          </Buttons>
        </Box>
      </Box>
      <CustomDataGrid
        columns={columns}
        rows={feeData}
        getRowId={(row: GridRowData) => row.dailyFeeId}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        currentPage="dailyFee"
        pageCount={pageCount}
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode) => {
          dispatch(setSortBy({ key: 'dailyFee', value: mode[0].field }));
          dispatch(setSortOrder({ key: 'dailyFee', value: mode[0].sort }));
          dispatch(setCurrentPage({ key: 'dailyFee', value: 0 }));
        }}
      />
      {openModal && (
        <DialogBox
          open={openModal}
          onClose={() => {
            handleCloseModal();
          }}
          title="Daily Fee"
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: '1450px',
              height: '90%',
            },
          }}
        >
          <Box>
            <Box mt={-4} padding={1}>
              <CustomDataGrid
                columns={modalColumns}
                rows={modalData}
                getRowId={(row) => row.dailyFeeId}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                }
                pageCount={modalPageCount}
                currentPage="dailyFeeModal"
                sortModel={checkSortingValues(modalPageInfo)}
                onSortModelChange={(mode) => {
                  dispatch(setCurrentPage({ key: 'dailyFeeModal', value: 0 }));
                  dispatch(
                    setSortBy({
                      key: 'dailyFeeModal',
                      value: mode[0]?.field ?? '',
                    }),
                  );
                  dispatch(
                    setSortOrder({
                      key: 'dailyFeeModal',
                      value: mode[0]?.sort ?? 'asc',
                    }),
                  );
                }}
                autoHeight={false}
              />
            </Box>
          </Box>
        </DialogBox>
      )}
    </Box>
  );
};

export default DailyFeeCalculation;
